import React from 'react';

import { fmt, Pill } from './parts';
import { BUS_COLOR, inScope, pathPowers } from './model';

/* The diagrams. Built from elements rather than one drawing, so the labels stay
   real text and every box is a real control. A wire with power on it shows
   moving flow; a dead one is a plain grey line, so the state reads at a glance. */

export const Wire = ({ vertical, off, reverse, short, children }) => (
    <span className={`dash-wire dash-wire--${vertical ? 'v' : 'h'}${off ? ' is-off' : ''}${reverse ? ' is-rev' : ''}${short ? ' is-short' : ''}`}>
        {children}
    </span>
);

export const Breaker = ({ closed, name }) => {
    const label = `${name} ${closed ? 'closed' : 'open'}`;

    return <i className={`dash-breaker${closed ? '' : ' is-open'}`} role="img" aria-label={label} title={label} />;
};

/** Legend keys for the diagrams, which draw things a chart legend does not. */
export function DiagramKey({ items }) {
    return (
        <ul className="dash-key">
            {items.map(item => (
                <li key={item.label}>
                    <i className={`dash-key__mark dash-key__mark--${item.mark}`} aria-hidden="true" />
                    {item.label}
                </li>
            ))}
        </ul>
    );
}

function BusHalf({ bus, dc, scope, onOpen }) {
    const data = dc.buses[bus];
    const live = data.mode !== 'down';
    const onBattery = data.mode === 'battery';
    const lower = bus === 'B';

    const source = (
        <div className={`dash-one__source${onBattery ? ' is-battery' : ''}`}>
            <strong>Source {bus}</strong>
            <span>Rectifier · {data.mode === 'normal' ? `${data.rectifier.online}/9 online` : data.rectifier.state}</span>
            <span>BESS {fmt(data.battery.soc, 0)}% · {data.battery.state.toLowerCase()}</span>
        </div>
    );

    const busRow = (
        <>
            {source}

            <div className="dash-one__feed">
                <Wire off={!live} />
                <Breaker closed={data.mainClosed} name={`Bus ${bus} main`} />
            </div>

            <div className="dash-one__bar">
                <div className="dash-one__readings">
                    <strong>Bus {bus}</strong>
                    <span>{fmt(data.volts)} VDC</span>
                    <span>{fmt(data.amps, 0)} A</span>
                    <span>{fmt(data.kw / 1000, 3)} MW</span>
                    <span>{fmt(data.loading)}% loaded</span>
                </div>
                <Wire off={!live} />
            </div>
        </>
    );

    const drops = data.feeders.map(feeder => {
        const condition = dc.conditions.find(c => c.feeder === feeder.id && c.kind === 'thermal');
        const closed = feeder.breaker === 'Closed';
        const name = `Feeder ${feeder.id} breaker`;

        return (
            <div className={`dash-one__drop${condition ? ' is-hot' : ''}`} key={feeder.id} style={{ gridColumn: feeder.index + 3 }}>
                <Wire vertical off={!closed} reverse={lower}>
                    <Breaker closed={closed} name={name} />
                </Wire>

                <button
                    type="button"
                    className={`dash-one__tap${condition ? ` is-${condition.severity}` : ''}`}
                    onClick={() => onOpen(feeder.id)}
                    aria-label={`Inspect feeder ${feeder.id}, ${fmt(feeder.kw)} kilowatts${condition ? `, ${condition.short}` : ''}`}
                >
                    {feeder.short}
                </button>

                <Wire vertical short off={!closed} reverse={lower} />

                <div className="dash-one__inv">
                    <strong>{feeder.inverter}</strong>
                    <span>{fmt(feeder.kw, 0)} kW DC</span>
                </div>
            </div>
        );
    });

    return (
        <div
            className={`dash-one__half dash-one__half--${bus.toLowerCase()}${inScope(scope, bus) ? '' : ' is-dim'}`}
            style={{ '--wire': live ? BUS_COLOR[bus] : 'var(--hairline-bright)' }}
        >
            {lower ? <>{drops}{busRow}</> : <>{busRow}{drops}</>}
        </div>
    );
}

/** Both independent DC buses as a one-line, source to inverter. */
export function BusDiagram({ dc, scope, onOpen }) {
    return (
        <div className="dash-one-wrap">
            <div className="dash-one">
                <BusHalf bus="A" dc={dc} scope={scope} onOpen={onOpen} />
                <BusHalf bus="B" dc={dc} scope={scope} onOpen={onOpen} />
            </div>
        </div>
    );
}

const STAGES = [
    { id: 'incomer', title: 'Utility incomer', stage: 'AC supply' },
    { id: 'rectifier', title: 'Rectifier input', stage: 'AC → DC' },
    { id: 'link', title: 'DC link', stage: '800 VDC' },
    { id: 'ups', title: 'Inverter output', stage: 'DC → AC' },
    { id: 'pdu', title: 'PDU / rack feed', stage: 'AC loads' }
];

const PATH_STATE = {
    normal: { tone: 'ok', label: 'Normal' },
    battery: { tone: 'crit', label: 'On battery' },
    down: { tone: 'crit', label: 'Out of service' }
};

/** One path as a chain of stages, from the utility to the rack. */
export function PathChain({ bus, dc, onOpen }) {
    const data = dc.buses[bus];
    const powers = pathPowers(bus, dc);
    const state = PATH_STATE[data.mode];

    const reading = stage => (stage === 'link' ? data.kw : powers[stage]);
    const carrying = stage => reading(stage) > 0;

    return (
        <div className="dash-chain" style={{ '--wire': BUS_COLOR[bus] }}>
            <div className="dash-chain__name">
                <strong>Path {bus}</strong>
                <Pill tone={state.tone}>{state.label}</Pill>
            </div>

            <ol className="dash-chain__stages">
                {STAGES.map((stage, i) => (
                    <li key={stage.id} className={carrying(stage.id) ? undefined : 'is-dead'}>
                        {i > 0 && <span className="dash-chain__link"><Wire off={!(carrying(stage.id) && carrying(STAGES[i - 1].id))} /></span>}

                        <button type="button" onClick={() => onOpen(bus, stage.id)} aria-label={`${stage.title} on path ${bus}, ${fmt(reading(stage.id))} kilowatts`}>
                            <span>{stage.title}</span>
                            <strong>{fmt(reading(stage.id), 0)}<small>kW</small></strong>
                            <em>{stage.stage}</em>
                        </button>
                    </li>
                ))}
            </ol>
        </div>
    );
}
